import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Globe2, Users, Trophy, Award, BarChart3, ArrowRight } from "lucide-react";
import Counter from "@/components/site/Counter";

const values = [
  { icon: ShieldCheck, t: "Trust", d: "Built on transparency and bank-grade security." },
  { icon: BarChart3, t: "Performance", d: "Proven outperformance across market cycles." },
  { icon: Globe2, t: "Global", d: "Cross-border investing across 41 countries." },
  { icon: Award, t: "Excellence", d: "Award-winning research and execution." },
];

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      <section className="relative py-24 sm:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(14,165,233,0.16),transparent_55%)]" />
        <div className="mfb-container relative">
          <div className="overline mb-4">About My Funding Bank</div>
          <h1 className="font-[Outfit] text-5xl sm:text-6xl font-light tracking-tighter text-white max-w-4xl leading-[1.05]">
            We&rsquo;re building the <span className="gradient-text-gold">private bank of the AI era</span>.
          </h1>
          <p className="mt-6 max-w-2xl text-slate-300 text-base leading-relaxed">
            My Funding Bank is a premium fintech designed for the next generation of investors —
            blending the trust of an institutional private bank with the speed and intelligence of modern AI.
          </p>
        </div>
      </section>

      <section className="mfb-section border-t border-white/5">
        <div className="mfb-container grid md:grid-cols-2 gap-16">
          <div>
            <div className="overline mb-3">Our Story</div>
            <h2 className="font-[Outfit] text-3xl text-white font-light tracking-tight">From conviction to category-defining platform</h2>
            <p className="mt-5 text-slate-400 leading-relaxed text-sm">
              Founded by veterans from Morgan Stanley, BlackRock and Goldman Sachs, MFB was born from a simple belief: that institutional-grade investing should not be reserved for the ultra-wealthy. Our platform delivers concierge wealth management — AI-engineered, globally available, and effortlessly mobile.
            </p>
          </div>
          <div>
            <div className="overline mb-3">Our Mission</div>
            <h2 className="font-[Outfit] text-3xl text-white font-light tracking-tight">To democratize the world&rsquo;s best capital strategies</h2>
            <p className="mt-5 text-slate-400 leading-relaxed text-sm">
              We exist to give every ambitious investor access to AI-curated portfolios, sovereign-grade gold, deep forex liquidity, and premium travel — from a single, beautifully designed account.
            </p>
          </div>
        </div>
      </section>

      <section className="mfb-section">
        <div className="mfb-container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {[
              { v: 12.4, p: "$", s: "B+", l: "Assets" },
              { v: 128, p: "", s: "K+", l: "Clients" },
              { v: 41, p: "", s: "+", l: "Countries" },
              { v: 6, p: "", s: " yrs", l: "Track record" },
            ].map((x, i) => (
              <div key={i} className="glass rounded-2xl p-6">
                <div className="font-[Outfit] text-3xl text-white font-light">
                  <Counter to={x.v} prefix={x.p} suffix={x.s} />
                </div>
                <div className="text-xs uppercase tracking-widest text-slate-400 mt-1">{x.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mfb-section border-t border-white/5">
        <div className="mfb-container">
          <div className="overline mb-3">Our Values</div>
          <h2 className="font-[Outfit] text-4xl font-light tracking-tight text-white max-w-2xl">Principles that compound, just like portfolios.</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
            {values.map((v, i) => (
              <div key={i} className="card-luxury p-7" data-testid={`value-${i}`}>
                <v.icon className="w-6 h-6 text-sky-400" />
                <div className="mt-4 text-white font-[Outfit] text-xl">{v.t}</div>
                <div className="mt-2 text-slate-400 text-sm leading-relaxed">{v.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mfb-section">
        <div className="mfb-container">
          <div className="rounded-3xl p-10 sm:p-14 glass relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(14,165,233,0.15),transparent_55%)]" />
            <div className="relative grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <Trophy className="w-7 h-7 text-[#D4AF37]" />
                <h2 className="mt-4 font-[Outfit] text-3xl sm:text-4xl font-light text-white tracking-tight">Recognized by the industry. Trusted by clients.</h2>
              </div>
              <div className="flex items-center gap-4">
                <Link to="/contact" className="btn-primary">Talk to us <ArrowRight className="w-4 h-4" /></Link>
                <Link to="/blog" className="btn-ghost">Read research</Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
